import React, { useEffect, useMemo, useState } from "react";
import MaterialIcon from "../../../components/MaterialIcon";
import EmailAssignModal from "../../../components/EmailAssignModal";
import { callAPI } from "../../../helper/apiUtils";
import { $crud } from "../../../helper/CrudFactory";
import { createInternalEmailRoom } from "../../../utils/emailAssignRoom";
import { classifyProjectWithProgress, classifySpamWithProgress } from "../../../utils/emailClassifyWithProgress";
import { fill, formatRelativeDay } from "../cockpitFormat";
import CockpitEmailReader from "./CockpitEmailReader";

const INITIAL_VISIBLE = 6;

/**
 * Inbox and sent mail on the cockpit, with the room each email was filed into.
 * Shown whether or not the email client is open inside Kjuup.
 * Assigning opens the same room / spam dialog as the email section.
 */
function senderLabel(item, strings) {
  const from = item?.fromAddress || {};
  return from.name || from.email || strings.unknown || "–";
}

function recipientLabel(item, strings) {
  const tos = Array.isArray(item?.toAddresses) ? item.toAddresses : [];
  const first = tos[0] || {};
  const who = first.name || first.email || "";
  if (!who) return strings.email_folder_sent || "Gesendet";
  const extra = tos.length > 1 ? ` +${tos.length - 1}` : "";
  return `${strings.email_to || "An"}: ${who}${extra}`;
}

function partyLabel(item, strings) {
  return item?.folder === "SENT" ? recipientLabel(item, strings) : senderLabel(item, strings);
}

function sameRooms(a, b) {
  const ida = (a || []).map((r) => String(r.id)).sort().join("|");
  const idb = (b || []).map((r) => String(r.id)).sort().join("|");
  return ida === idb;
}

const EMPTY_LIST = Object.freeze([]);

function asAddressList(raw) {
  const rows = Array.isArray(raw) ? raw : [];
  return rows
    .map((t) => {
      if (!t || typeof t !== "object") return null;
      const email = t.email || t.address || "";
      const name = t.name || "";
      if (!email && !name) return null;
      return { name, email };
    })
    .filter(Boolean);
}

function isDraft(item) {
  const labels = Array.isArray(item?.labels) ? item.labels : [];
  return labels.some((l) => String(l || "").toUpperCase() === "DRAFT");
}

/** Mailbox rows from GET /email/messages, shaped like the cockpit overview items. */
function mailboxMessageToItem(message, folder) {
  const rooms = (Array.isArray(message.linkedProjects) ? message.linkedProjects : []).map((p) => ({
    id: p.id,
    name: p.name || "",
    themeColor: p.themeColor || null,
  }));
  return {
    id: message.id,
    folder: folder === "SENT" ? "SENT" : "INBOX",
    subject: message.subject || "",
    snippet: message.snippet || "",
    fromAddress: message.fromAddress && typeof message.fromAddress === "object"
      ? { name: message.fromAddress.name || "", email: message.fromAddress.email || message.fromAddress.address || "" }
      : null,
    toAddresses: asAddressList(message.toAddresses),
    receivedAt: message.receivedAt || message.createdAt || null,
    isRead: !!message.isRead,
    hasAttachments: !!message.hasAttachments,
    emailAccountId: message.emailAccount || message.emailAccountId || null,
    needsClassification: !!message.needsClassification,
    proposedBuilding: null,
    rooms,
    labels: message.labels,
  };
}

export default function CockpitIncomingEmails({ strings, language, items, onChanged }) {
  const list = useMemo(() => (Array.isArray(items) ? items : EMPTY_LIST), [items]);
  const overviewHasInbox = useMemo(() => list.some((item) => item && item.folder !== "SENT"), [list]);
  const overviewHasSent = useMemo(() => list.some((item) => item && item.folder === "SENT"), [list]);
  const [extraFetched, setExtraFetched] = useState(EMPTY_LIST);
  const merged = useMemo(() => {
    if (!extraFetched.length) return list;
    const known = new Set(list.map((item) => String(item && item.id)));
    const extra = extraFetched.filter((item) => item && item.id && !known.has(String(item.id)));
    return extra.length ? [...list, ...extra] : list;
  }, [list, extraFetched]);
  const hasItems = merged.length > 0;
  const [roomOptions, setRoomOptions] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [folder, setFolder] = useState("INBOX");
  const [assignTarget, setAssignTarget] = useState(null);
  const [reading, setReading] = useState(null);
  const [removedIds, setRemovedIds] = useState({});
  // Optimistic room state per email id until the next overview confirms it.
  const [overrides, setOverrides] = useState({});

  // The overview includes both folders once the API does. Until then, and
  // whenever one folder is missing, load that folder from the mailbox list.
  const listKey = useMemo(() => list.map((item) => String(item && item.id)).join(","), [list]);
  useEffect(() => {
    const missing = [];
    if (!overviewHasInbox) missing.push("INBOX");
    if (!overviewHasSent) missing.push("SENT");
    if (!missing.length) {
      setExtraFetched(EMPTY_LIST);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const batches = await Promise.all(
          missing.map(async (folder) => {
            const res = await callAPI(`/email/messages`, { folder, limit: 25, page: 1 }, "GET");
            const data = res?.data || res;
            const messages = data?.messages && Array.isArray(data.messages) ? data.messages : [];
            return messages
              .map((message) => mailboxMessageToItem(message, folder))
              .filter((item) => item.id && (folder !== "SENT" || !isDraft(item)));
          })
        );
        if (!cancelled) setExtraFetched(batches.reduce((all, rows) => all.concat(rows), []));
      } catch (_) {
        if (!cancelled) setExtraFetched(EMPTY_LIST);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [overviewHasInbox, overviewHasSent, listKey]);

  // Assignable rooms for the picker (joined, not completed / archived).
  useEffect(() => {
    if (!hasItems) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await callAPI(`/email/classification/buildings`, {}, "GET");
        const data = res?.data || res;
        const rows = data?.buildings && Array.isArray(data.buildings) ? data.buildings : [];
        if (!cancelled) {
          setRoomOptions(
            rows.map((b) => ({
              id: b.id,
              name: b.name || "",
              themeColor: b.themeColor || null,
              projectNo: b.projectNo || "",
              aliases: Array.isArray(b.aliases) ? b.aliases : [],
            }))
          );
        }
      } catch (_) {
        /* picker falls back to the rooms already referenced by the list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasItems]);

  // Drop optimistic overrides once the server list reflects them.
  useEffect(() => {
    setOverrides((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const next = { ...prev };
      let changed = false;
      for (const item of merged) {
        const key = String(item.id);
        if (next[key] && sameRooms(next[key], item.rooms)) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [merged]);

  const rows = useMemo(
    () =>
      merged
        .filter((item) => item && item.id && !removedIds[String(item.id)])
        .map((item) => {
          const rooms = overrides[String(item.id)] || item.rooms || [];
          return { item, rooms, room: rooms[0] || null };
        }),
    [merged, overrides, removedIds]
  );
  const folderRows = useMemo(
    () => rows.filter((r) => (r.item.folder === "SENT" ? "SENT" : "INBOX") === folder),
    [rows, folder]
  );
  const unassignedCount = folderRows.filter((r) => !r.room).length;
  const visible = expanded ? folderRows : folderRows.slice(0, INITIAL_VISIBLE);

  const notifyError = (e) =>
    $crud.notify({
      type: "error",
      message: (e && e.data && e.data.message) || (e && e.message) || strings.something_went_wrong || "Something went wrong",
    });

  const assign = async (item, room) => {
    if (!room || savingId) return;
    setSavingId(item.id);
    try {
      const { data } = await classifyProjectWithProgress({
        messageId: item.id,
        body: {
          buildingId: room.id,
          buildingName: room.name,
          folder: item.folder === "SENT" ? "SENT" : "INBOX",
          accountId: item.emailAccountId || undefined,
          // Every email is re-classified on its own — no sticky sender rule.
          rememberSender: false,
          applyToAccount: false,
        },
      });
      const saved = (data && data.building) || room;
      setOverrides((prev) => ({
        ...prev,
        [String(item.id)]: [{ id: saved.id, name: saved.name || room.name, themeColor: saved.themeColor || room.themeColor || null }],
      }));
      $crud.notify({ type: "success", message: strings.email_saved_to_project || "E-Mail im Projekt gespeichert" });
      if (onChanged) onChanged();
    } catch (e) {
      notifyError(e);
    } finally {
      setSavingId(null);
    }
  };

  const classifyFromModal = async (messageId, decision, projectName, buildingId, companyId, isInternal, onProgress) => {
    const item = assignTarget && assignTarget.item;
    const id = (item && item.id) || messageId;
    if (!item || !id || savingId) return;
    const mailFolder = item.folder === "SENT" ? "SENT" : "INBOX";
    const extra = { folder: mailFolder, accountId: item.emailAccountId || undefined };
    setSavingId(id);

    if (decision === "spam") {
      try {
        await classifySpamWithProgress({ messageId: id, onProgress, extra });
        setRemovedIds((prev) => ({ ...prev, [String(id)]: true }));
        if (onChanged) onChanged();
      } finally {
        setSavingId(null);
      }
      return;
    }

    const name = (projectName || "").trim();
    if (!name) {
      setSavingId(null);
      $crud.notify({ type: "error", message: strings.email_classify_need_project || "Bitte einen Projektnamen eingeben" });
      throw new Error(strings.email_classify_need_project || "Bitte einen Projektnamen eingeben");
    }

    try {
      let matched = null;
      if (buildingId) {
        matched = roomOptions.find((r) => String(r.id) === String(buildingId)) || { id: buildingId, name };
      } else if (isInternal) {
        matched = await createInternalEmailRoom(name, companyId);
      } else {
        matched = roomOptions.find((r) => (r.name || "").trim().toLowerCase() === name.toLowerCase()) || null;
      }

      const body = {
        messageId: id,
        buildingName: name,
        rememberSender: false,
        applyToAccount: false,
        ...extra,
      };
      if (matched) body.buildingId = matched.id;
      else if (companyId) body.companyId = companyId;
      if (!matched && isInternal) body.isInternal = true;

      const { data } = await classifyProjectWithProgress({ messageId: id, body, onProgress });
      const saved = (data && data.building) || matched;
      if (saved && saved.id) {
        setOverrides((prev) => ({
          ...prev,
          [String(id)]: [{ id: saved.id, name: saved.name || name, themeColor: saved.themeColor || null }],
        }));
      }
      $crud.notify({ type: "success", message: strings.email_saved_to_project || "E-Mail im Projekt gespeichert" });
      if (onChanged) onChanged();
    } finally {
      setSavingId(null);
    }
  };

  const removeEmail = async (item) => {
    if (!item || savingId) return;
    try {
      await $crud.confirm({
        title: strings.email_delete || "Löschen",
        textContent: strings.email_delete_confirm || "Sind Sie sicher, dass Sie diese E-Mail löschen möchten?",
        options: { ok: strings.ok || "OK", cancel: strings.cancel || "Abbrechen" },
      });
    } catch (_) {
      return;
    }
    setSavingId(item.id);
    try {
      const res = await callAPI(`/email/messages/${encodeURIComponent(item.id)}`, {}, "DELETE");
      const data = res?.data || res;
      if (!data || data.status === false) {
        throw new Error((data && data.message) || strings.email_delete_email_failed || "E-Mail konnte nicht gelöscht werden.");
      }
      setRemovedIds((prev) => ({ ...prev, [String(item.id)]: true }));
      $crud.notify({ type: "success", message: strings.cockpit_email_deleted || "E-Mail gelöscht" });
      if (onChanged) onChanged();
    } catch (e) {
      notifyError(e);
    } finally {
      setSavingId(null);
    }
  };

  const switchFolder = (next) => {
    setFolder(next);
    setExpanded(false);
  };

  const emptyText =
    folder === "SENT"
      ? strings.cockpit_email_sent_empty || "Noch keine gesendeten E-Mails."
      : strings.cockpit_email_incoming_empty || "Noch keine eingehenden E-Mails.";

  return (
    <div className="cockpit-inmail">
      <div className="cockpit-inmail-head">
        <div className="cockpit-inmail-head-text">
          <div className="cockpit-inmail-title">{strings.cockpit_email_incoming_title || "E-Mails"}</div>
          <div className="cockpit-inmail-sub">
            {strings.cockpit_email_incoming_sub ||
              "Ordne eingehende und gesendete E-Mails deinen Projekten zu. Die Zuordnung kannst du hier anpassen."}
          </div>
        </div>
        {unassignedCount > 0 ? (
          <span className="cockpit-chip cockpit-chip--red">
            {fill(strings.cockpit_email_incoming_unassigned_n || "{{n}} nicht zugeordnet", { n: unassignedCount })}
          </span>
        ) : null}
      </div>

      <div className="cockpit-inmail-folders" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={folder === "INBOX"}
          className={`kj-tab${folder === "INBOX" ? " is-active" : ""}`}
          onClick={() => switchFolder("INBOX")}
        >
          {strings.cockpit_email_folder_inbox || "Eingang"}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={folder === "SENT"}
          className={`kj-tab${folder === "SENT" ? " is-active" : ""}`}
          onClick={() => switchFolder("SENT")}
        >
          {strings.cockpit_email_folder_sent || "Gesendet"}
        </button>
      </div>

      {!folderRows.length ? (
        <div className="cockpit-inmail-empty">{emptyText}</div>
      ) : (
        <ul className="cockpit-inmail-list">
          {visible.map((row) => {
            const { item, room } = row;
            const saving = savingId === item.id;
            const subject = item.subject || strings.email_no_subject || "(Kein Betreff)";
            const proposal = !room && item.proposedBuilding ? item.proposedBuilding : null;
            const who = partyLabel(item, strings);
            const open = reading && String(reading.id) === String(item.id);
            const toggle = () => setReading(open ? null : item);
            return (
              <li key={item.id} className={`cockpit-inmail-row${open ? " cockpit-inmail-row--open" : ""}${room ? "" : " cockpit-inmail-row--unassigned"}${item.isRead ? "" : " cockpit-inmail-row--unread"}`}>
                <div className="cockpit-inmail-bar">
                <div className="cockpit-inmail-main">
                  <div
                    className="cockpit-inmail-open-hit"
                    role="button"
                    tabIndex={0}
                    title={strings.cockpit_email_open || "E-Mail öffnen"}
                    aria-expanded={open}
                    onClick={toggle}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle();
                      }
                    }}
                  >
                    <div className="cockpit-inmail-line">
                      <span className="cockpit-inmail-from" title={item.folder === "SENT" ? item.toAddresses?.[0]?.email : item.fromAddress?.email || undefined}>
                        {who}
                      </span>
                      {item.hasAttachments ? <MaterialIcon icon="attach_file" size={13} color="currentColor" weight={400} /> : null}
                      <span className="cockpit-inmail-time">{formatRelativeDay(item.receivedAt, strings, language)}</span>
                    </div>
                    <div className="cockpit-inmail-subject" title={subject}>
                      {subject}
                    </div>
                  </div>
                  {proposal ? (
                    <div className="cockpit-inmail-proposal">
                      <MaterialIcon icon="auto_awesome" size={13} color="currentColor" weight={400} />
                      <span>{fill(strings.cockpit_email_incoming_suggested || "Vorschlag: {{room}}", { room: proposal.name })}</span>
                      <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm" disabled={!!savingId} onClick={() => assign(item, proposal)}>
                        <span>{strings.cockpit_email_incoming_apply || "Übernehmen"}</span>
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="cockpit-inmail-actions">
                  <button
                    type="button"
                    className={`cockpit-inmail-picker${room ? "" : " cockpit-inmail-picker--none"}${saving ? " cockpit-inmail-picker--saving" : ""}`}
                    title={strings.cockpit_email_incoming_adjust || "Zuordnung anpassen"}
                    disabled={!!savingId}
                    onClick={() => setAssignTarget({ item, proposedName: room ? room.name : proposal ? proposal.name : "" })}
                  >
                    <span
                      className={`cockpit-inmail-dot${room && room.themeColor ? " cockpit-inmail-dot--tinted" : ""}`}
                      style={room && room.themeColor ? { "--cockpit-room-color": room.themeColor } : undefined}
                    />
                    <span className="cockpit-inmail-picker-label">
                      {saving
                        ? strings.cockpit_email_incoming_saving || "Speichern …"
                        : room
                        ? room.name
                        : strings.cockpit_email_incoming_unassigned || "Nicht zugeordnet"}
                    </span>
                    <MaterialIcon icon={saving ? "progress_activity" : "expand_more"} size={16} color="currentColor" weight={400} />
                  </button>
                  <button
                    type="button"
                    className="kj-btn kj-btn--icon kj-btn--neutral kj-btn--danger cockpit-inmail-delete"
                    title={strings.email_delete || "Löschen"}
                    aria-label={strings.email_delete || "Löschen"}
                    disabled={!!savingId}
                    onClick={() => removeEmail(item)}
                  >
                    <MaterialIcon icon="delete" size={18} color="currentColor" weight={400} />
                  </button>
                  <button
                    type="button"
                    className="kj-btn kj-btn--icon kj-btn--neutral cockpit-inmail-open"
                    title={strings.cockpit_email_open || "E-Mail öffnen"}
                    aria-label={strings.cockpit_email_open || "E-Mail öffnen"}
                    aria-expanded={open}
                    onClick={toggle}
                  >
                    <MaterialIcon icon={open ? "keyboard_arrow_up" : "keyboard_arrow_down"} size={22} color="currentColor" weight={400} />
                  </button>
                </div>
                </div>
                {open ? (
                  <CockpitEmailReader item={reading} strings={strings} language={language} />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {folderRows.length > INITIAL_VISIBLE ? (
        <button type="button" className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm cockpit-inmail-more" onClick={() => setExpanded((v) => !v)}>
          <span>
            {expanded
              ? strings.cockpit_email_incoming_show_less || "Weniger anzeigen"
              : fill(strings.cockpit_email_incoming_show_all || "Alle {{n}} anzeigen", { n: folderRows.length })}
          </span>
          <MaterialIcon icon={expanded ? "expand_less" : "expand_more"} size={14} color="currentColor" weight={400} />
        </button>
      ) : null}

      {assignTarget ? (
        <EmailAssignModal
          email={assignTarget.item}
          proposedName={assignTarget.proposedName || ""}
          buildings={roomOptions}
          onClassify={classifyFromModal}
          onClose={() => setAssignTarget(null)}
          strings={strings}
        />
      ) : null}
    </div>
  );
}
